/**
 * @fileoverview The fields of the Edit Prospect dialog.
 * @description The five sections of New Prospect (basic info, address, organisation, primary
 * contact, sales info) laid out on one scrolling form, filled with the prospect's current values.
 * The bank's registration number and type are its identity, so they are shown read-only; the owner
 * is not here at all (it changes through Assign Owner). Labels reuse New Prospect's translations.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import FormField from '@/shared/components/molecules/FormField'
import SelectField from '@/shared/components/molecules/SelectField'
import { MOBILE_NUMBER_INPUT_PROPS } from '@/shared/lib/validation/contact-fields.constants'

import type { EditProspectDetailsFormValues } from '@/features/sales/schemas/edit-prospect-details-form.schema'
import type { ProspectMetadata } from '@/features/sales/types/prospect.types'
import type { FieldErrors, UseFormRegister } from 'react-hook-form'

interface EditProspectDetailsFieldsProps {
  register: UseFormRegister<EditProspectDetailsFormValues>
  errors: FieldErrors<EditProspectDetailsFormValues>
  metadata: ProspectMetadata
  /** The tier the current branch count falls into. */
  previewTier: string
  /** Whether the bank type and registration number can change (a client; a prospect's cannot). */
  identityEditable?: boolean
  /** The prefix of every field's test id. */
  testIdPrefix?: string
}

/**
 * @description Appends a required-field marker to a label.
 * @param {string} label - The field's translated label.
 * @returns {string} The label with a trailing " *".
 */
function req(label: string): string {
  return `${label} *`
}

/**
 * @component
 * @param {EditProspectDetailsFieldsProps} props - The form's wiring and the catalogue choices.
 * @param {UseFormRegister<EditProspectDetailsFormValues>} props.register - React Hook Form's register.
 * @param {FieldErrors<EditProspectDetailsFormValues>} props.errors - The current validation errors.
 * @param {ProspectMetadata} props.metadata - The prospect metadata (bank types, states, …).
 * @param {string} props.previewTier - The tier the branch count falls into.
 * @param {boolean} [props.identityEditable] - Whether the bank type and registration number can
 * change; false by default.
 * @param {string} [props.testIdPrefix] - The prefix of every field's test id.
 */
function EditProspectDetailsFields({
  register,
  errors,
  metadata,
  previewTier,
  identityEditable = false,
  testIdPrefix = 'edit-prospect',
}: EditProspectDetailsFieldsProps) {
  const { t } = useTranslation()
  /**
   * @description Translates a create-form field label by key.
   * @param {string} key - The field key under `sales.pipeline.create.fields`.
   * @returns {string} The label.
   */
  const f = (key: string) => t(`sales.pipeline.create.fields.${key}`)
  /**
   * @description Translates a field's validation message, if it has one.
   * @param {keyof EditProspectDetailsFormValues} key - The field.
   * @returns {string | undefined} The message, or undefined when the field is valid.
   */
  const message = (key: keyof EditProspectDetailsFormValues) => {
    const text = errors[key]?.message
    return text ? t(text) : undefined
  }
  /**
   * @description Turns catalogue entries into select options behind a placeholder.
   * @param {{ id: string; name: string }[]} entries - The catalogue choices.
   * @returns {{ value: string; label: string }[]} The options.
   */
  const toOptions = (entries: { id: string; name: string }[]) => [
    { value: '', label: t('sales.pipeline.form.selectPlaceholder') },
    ...entries.map((entry) => ({ value: entry.id, label: entry.name })),
  ]
  /**
   * @description Renders a form section heading.
   * @param {string} key - The section key under `sales.pipeline.create.steps`.
   * @returns {JSX.Element} The heading.
   */
  const section = (key: string) => (
    <h3 className="col-span-full border-b border-divider pb-1.5 text-[12px] font-bold tracking-[0.6px] text-info">
      {t(`sales.pipeline.create.steps.${key}`)}
    </h3>
  )

  return (
    <>
      {section('basicInfo')}
      <div className="col-span-full">
        <FormField
          id="editName"
          data-testid={`${testIdPrefix}-name-input`}
          size="md"
          label={req(f('name'))}
          error={message('name')}
          {...register('name')}
        />
      </div>
      <SelectField
        id="editBankType"
        data-testid={`${testIdPrefix}-bank-type-select`}
        label={f('bankType')}
        disabled={!identityEditable}
        options={toOptions(metadata.bankTypes)}
        {...register('bankTypeId')}
      />
      <FormField
        id="editRegNumber"
        data-testid={`${testIdPrefix}-reg-number-input`}
        size="md"
        label={f('regNumber')}
        readOnly={!identityEditable}
        {...register('regNumber')}
      />
      <FormField
        id="editHeadOffice"
        data-testid={`${testIdPrefix}-head-office-input`}
        size="md"
        label={req(f('headOffice'))}
        error={message('headOffice')}
        {...register('headOffice')}
      />
      <FormField
        id="editWebsite"
        data-testid={`${testIdPrefix}-website-input`}
        size="md"
        label={f('website')}
        {...register('website')}
      />

      {section('address')}
      <div className="col-span-full">
        <FormField
          id="editAddress"
          data-testid={`${testIdPrefix}-address-input`}
          size="md"
          label={req(f('address'))}
          error={message('address')}
          {...register('address')}
        />
      </div>
      <SelectField
        id="editState"
        data-testid={`${testIdPrefix}-state-select`}
        label={req(f('state'))}
        options={toOptions(metadata.states)}
        error={message('stateId')}
        {...register('stateId')}
      />
      <FormField
        id="editCity"
        data-testid={`${testIdPrefix}-city-input`}
        size="md"
        label={req(f('city'))}
        error={message('city')}
        {...register('city')}
      />
      <FormField
        id="editDistrict"
        data-testid={`${testIdPrefix}-district-input`}
        size="md"
        label={req(f('district'))}
        error={message('district')}
        {...register('district')}
      />
      <FormField
        id="editPincode"
        data-testid={`${testIdPrefix}-pincode-input`}
        size="md"
        label={req(f('pincode'))}
        error={message('pincode')}
        inputMode="numeric"
        maxLength={6}
        {...register('pincode')}
      />

      {section('organisation')}
      <FormField
        id="editBranches"
        data-testid={`${testIdPrefix}-branches-input`}
        size="md"
        type="number"
        label={req(f('branches'))}
        error={message('branches')}
        {...register('branches', { valueAsNumber: true })}
      />
      <FormField
        id="editEmployees"
        data-testid={`${testIdPrefix}-employees-input`}
        size="md"
        type="number"
        label={f('employees')}
        {...register('employees', {
          setValueAs: (value) => (value === '' || value === null ? undefined : Number(value)),
        })}
      />
      <div>
        <SelectField
          id="editBankTier"
          data-testid={`${testIdPrefix}-bank-tier-select`}
          label={f('bankTier')}
          disabled
          value={previewTier}
          onChange={() => {}}
          options={[{ value: previewTier, label: previewTier }]}
        />
        <p className="mt-1.5 text-[12px] text-slate">{f('bankTierHint')}</p>
      </div>
      <SelectField
        id="editCoreBanking"
        data-testid={`${testIdPrefix}-core-banking-select`}
        label={req(f('coreBanking'))}
        options={toOptions(metadata.coreBankingSolutions)}
        error={message('coreBankingSolutionId')}
        {...register('coreBankingSolutionId')}
      />
      <FormField
        id="editExistingMis"
        data-testid={`${testIdPrefix}-existing-mis-input`}
        size="md"
        label={f('existingMis')}
        {...register('existingMis')}
      />

      {section('primaryContact')}
      <FormField
        id="editContactName"
        data-testid={`${testIdPrefix}-contact-name-input`}
        size="md"
        label={req(f('contactName'))}
        error={message('contactName')}
        {...register('contactName')}
      />
      <FormField
        id="editContactDesignation"
        data-testid={`${testIdPrefix}-contact-designation-input`}
        size="md"
        label={f('contactDesignation')}
        {...register('contactDesignation')}
      />
      <FormField
        id="editContactEmail"
        data-testid={`${testIdPrefix}-contact-email-input`}
        size="md"
        type="email"
        label={req(f('contactEmail'))}
        error={message('contactEmail')}
        {...register('contactEmail')}
      />
      <FormField
        id="editContactPhone"
        data-testid={`${testIdPrefix}-contact-phone-input`}
        size="md"
        label={req(f('contactPhone'))}
        error={message('contactPhone')}
        {...MOBILE_NUMBER_INPUT_PROPS}
        {...register('contactPhone')}
      />

      {section('salesInfo')}
      <SelectField
        id="editLeadSource"
        data-testid={`${testIdPrefix}-lead-source-select`}
        label={req(f('leadSource'))}
        options={toOptions(metadata.leadSources)}
        error={message('leadSourceId')}
        {...register('leadSourceId')}
      />
      <SelectField
        id="editLeadRating"
        data-testid={`${testIdPrefix}-lead-rating-select`}
        label={req(f('leadRating'))}
        options={metadata.leadRatings.map((rating) => ({
          value: rating,
          label: t(`sales.pipeline.ratings.${rating}`),
        }))}
        error={message('leadRating')}
        {...register('leadRating')}
      />
      <div className="col-span-full">
        <FormField
          id="editNotes"
          data-testid={`${testIdPrefix}-notes-input`}
          size="md"
          label={t('sales.pipeline.edit.addNote')}
          {...register('notes')}
        />
      </div>
    </>
  )
}

export default EditProspectDetailsFields
